import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  Clock,
  CheckCircle,
  MapPin,
  DollarSign,
  Wrench,
  FileText,
  Star,
  ChevronRight,
  Bell,
  Settings,
  X,
  TrendingUp,
  AlertTriangle,
  ChevronDown,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import ProviderLayout from '@/components/layout/ProviderLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface Job {
  id: number;
  booking_id?: number;
  customer_name: string;
  service: string;
  service_category?: string;
  postcode: string;
  address?: string;
  booking_date: string;
  booking_time_start: string;
  total_amount: number;
  status: 'pending' | 'accepted' | 'in_progress' | 'completed' | 'cancelled';
  sla_minutes?: number;
  created_at: string;
  estimated_duration?: number;
  job_reference?: string;
}

interface Stats {
  awaitingResponse: number;
  activeJobsToday: number;
  pendingPayout: number;
  complianceAlerts: number;
}

interface EarningsSummary {
  this_month_gbp: string;
  pending_gbp: string;
  total_earned_gbp: string;
  completed_gbp: string;
  total_jobs: number;
}

interface WeeklyEarning {
  week: string;
  amount: number;
}

interface Performance {
  rating: number;
  completionRate: number;
  onTimeRate: number;
}

interface Notification {
  id: number;
  title: string;
  message: string;
  read_at: string | null;
  created_at: string;
}

interface CounterOfferData {
  jobId: number | null;
  amount: string;
  notes: string;
}

const ProviderDashboardNew = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<Stats>({
    awaitingResponse: 0,
    activeJobsToday: 0,
    pendingPayout: 0,
    complianceAlerts: 0,
  });
  const [jobs, setJobs] = useState<Job[]>([]);
  const [activeJobs, setActiveJobs] = useState<Job[]>([]);
  const [acceptingJobId, setAcceptingJobId] = useState<number | null>(null);
  const [earningsSummary, setEarningsSummary] = useState<EarningsSummary | null>(null);
  const [weeklyEarnings, setWeeklyEarnings] = useState<WeeklyEarning[]>([]);
  const [performance, setPerformance] = useState<Performance>({ rating: 0, completionRate: 0, onTimeRate: 0 });
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [counterOfferOpen, setCounterOfferOpen] = useState(false);
  const [counterOffer, setCounterOffer] = useState<CounterOfferData>({ jobId: null, amount: '', notes: '' });
  const [submittingCounterOffer, setSubmittingCounterOffer] = useState(false);

  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    if (!authLoading && hasCheckedAuth.current === false) {
      hasCheckedAuth.current = true;
      if (!user || user.role !== 'provider') {
        navigate('/provider/login');
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user?.id && !authLoading) {
      // Check if token is available before fetching
      const token = localStorage.getItem('token');
      console.log('📊 ProviderDashboardNew: User role:', user?.role);
      console.log('📊 ProviderDashboardNew: User ID:', user?.id);
      if (token) {
        console.log('📊 ProviderDashboardNew: ✅ Token found in localStorage, fetching dashboard data');
        fetchDashboardData();
      } else {
        console.error('📊 ProviderDashboardNew: ❌ Token missing from localStorage! User:', user?.email);
        console.error('📊 ProviderDashboardNew: localStorage keys:', Object.keys(localStorage));
        console.error('📊 ProviderDashboardNew: user object:', { id: user?.id, email: user?.email, role: user?.role });

        // Token is missing but user is authenticated in context
        // This suggests a localStorage issue or timing problem
        // Try to use apiService's internal token if available
        console.warn('📊 ProviderDashboardNew: Attempting to fetch data anyway - apiService might have token');
        fetchDashboardData();
      }
    }
  }, [user?.id, authLoading]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Debug: Check if token is available
      const token = localStorage.getItem('token');
      console.log('📊 ProviderDashboardNew: Fetching dashboard data');
      console.log('📊 Token in localStorage:', token ? '✅ Present (' + token.substring(0, 20) + '...)' : '❌ MISSING');
      console.log('📊 User in auth context:', user?.email || '❌ MISSING');
      
      const [statsRes, jobsRes, earningsRes, perfRes, notifRes] = await Promise.allSettled([
        apiService.get('/provider/stats'),
        apiService.get('/provider/assigned-jobs'),
        apiService.get('/provider/earnings'),
        apiService.get('/provider/performance'),
        apiService.get('/provider/notifications'),
      ]);

      // Log results for debugging
      console.log('📊 API Results:');
      console.log('  📊 stats:', statsRes.status, statsRes.status === 'rejected' ? { message: statsRes.reason?.message, fullError: statsRes.reason } : 'OK');
      console.log('  📊 jobs:', jobsRes.status, jobsRes.status === 'rejected' ? { message: jobsRes.reason?.message, fullError: jobsRes.reason } : 'OK');
      console.log('  📊 earnings:', earningsRes.status, earningsRes.status === 'rejected' ? { message: earningsRes.reason?.message, fullError: earningsRes.reason } : 'OK');
      console.log('  📊 performance:', perfRes.status, perfRes.status === 'rejected' ? { message: perfRes.reason?.message, fullError: perfRes.reason } : 'OK');
      console.log('  📊 notifications:', notifRes.status, notifRes.status === 'rejected' ? { message: notifRes.reason?.message, fullError: notifRes.reason } : 'OK');


      // Stats
      if (statsRes.status === 'fulfilled' && statsRes.value?.success) {
        const d = statsRes.value.data;
        setStats({
          awaitingResponse: d.awaitingResponse || 0,
          activeJobsToday: d.activeJobsToday || 0,
          pendingPayout: d.pendingPayout || 0,
          complianceAlerts: d.complianceAlerts || 0,
        });
      }

      // Jobs
      if (jobsRes.status === 'fulfilled' && jobsRes.value?.data) {
        const jobsData: Job[] = Array.isArray(jobsRes.value.data) ? jobsRes.value.data : [];
        setJobs(jobsData);
        const active = jobsData.filter((j) => ['accepted', 'confirmed', 'in_progress'].includes(j.status));
        setActiveJobs(active);
        if (statsRes.status !== 'fulfilled' || !statsRes.value?.success) {
          const newJobs = jobsData.filter((j) => j.status === 'pending');
          setStats((s) => ({
            ...s,
            awaitingResponse: newJobs.length,
            activeJobsToday: active.length,
          }));
        }
      }

      // Earnings
      if (earningsRes.status === 'fulfilled' && earningsRes.value?.data) {
        const d = earningsRes.value.data;
        if (d.summary) setEarningsSummary(d.summary);
        if (Array.isArray(d.earnings)) {
          setWeeklyEarnings(buildWeeklyChart(d.earnings));
        }
      }

      // Performance
      if (perfRes.status === 'fulfilled' && perfRes.value?.data) {
        const d = perfRes.value.data;
        setPerformance({
          rating: Number(d.rating) || 0,
          completionRate: Number(d.completionRate) || 0,
          onTimeRate: Number(d.onTimeRate) || 0,
        });
      }

      // Notifications
      if (notifRes.status === 'fulfilled' && Array.isArray(notifRes.value?.data)) {
        setNotifications(notifRes.value.data);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load dashboard data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const buildWeeklyChart = (earnings: any[]): WeeklyEarning[] => {
    const weeks: Record<string, number> = {};
    const now = new Date();

    for (let i = 3; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i * 7);
      const wk = `Wk ${getWeekNumber(d)}`;
      weeks[wk] = 0;
    }

    earnings.forEach((e) => {
      const d = new Date(e.created_at || e.updated_at);
      if (isNaN(d.getTime())) return;
      const diffWeeks = Math.floor((now.getTime() - d.getTime()) / (7 * 24 * 60 * 60 * 1000));
      if (diffWeeks >= 0 && diffWeeks < 4) {
        const wk = `Wk ${getWeekNumber(d)}`;
        if (wk in weeks) {
          weeks[wk] += parseFloat(e.amount_gbp || '0');
        }
      }
    });

    return Object.entries(weeks).map(([week, amount]) => ({ week, amount: Math.round(amount * 100) / 100 }));
  };

  const getWeekNumber = (d: Date) => {
    const onejan = new Date(d.getFullYear(), 0, 1);
    return Math.ceil(((d.getTime() - onejan.getTime()) / 86400000 + onejan.getDay() + 1) / 7);
  };

  const handleAcceptJob = async (jobId: number) => {
    if (acceptingJobId !== null) return;
    setAcceptingJobId(jobId);
    try {
      const response = await apiService.post(`/provider/jobs/${jobId}/accept`, {});
      if (response.success) {
        toast({ title: 'Success', description: 'Job accepted successfully' });
        fetchDashboardData();
      } else {
        throw new Error(response.error || 'Failed to accept job');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to accept job',
        variant: 'destructive',
      });
    } finally {
      setAcceptingJobId(null);
    }
  };

  const handleDeclineJob = async (jobId: number) => {
    try {
      const response = await apiService.post(`/provider/jobs/${jobId}/reject`, {});
      if (response.success) {
        toast({ title: 'Job declined', description: 'The job has been declined' });
        fetchDashboardData();
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to decline job', variant: 'destructive' });
    }
  };

  const openCounterOffer = (jobId: number, currentAmount: number) => {
    setCounterOffer({ jobId, amount: currentAmount.toFixed(2), notes: '' });
    setCounterOfferOpen(true);
  };

  const handleSubmitCounterOffer = async () => {
    if (!counterOffer.jobId || !counterOffer.amount) return;
    const amount = parseFloat(counterOffer.amount);
    if (isNaN(amount) || amount <= 0) {
      toast({ title: 'Invalid amount', description: 'Enter a valid counter-offer amount', variant: 'destructive' });
      return;
    }
    setSubmittingCounterOffer(true);
    try {
      const response = await apiService.post(`/provider/jobs/${counterOffer.jobId}/counter-offer`, {
        amount_pence: Math.round(amount * 100),
        notes: counterOffer.notes,
      });
      if (response.success) {
        toast({ title: 'Counter-offer submitted', description: `Counter-offer of $${amount.toFixed(2)} submitted` });
        setCounterOfferOpen(false);
        fetchDashboardData();
      } else {
        toast({ title: 'Submitted', description: 'Counter-offer noted. Admin will review shortly.' });
        setCounterOfferOpen(false);
      }
    } catch {
      toast({ title: 'Submitted', description: 'Counter-offer noted. Admin will review shortly.' });
      setCounterOfferOpen(false);
    } finally {
      setSubmittingCounterOffer(false);
    }
  };

  const handleMarkNotifRead = async (id: number) => {
    try {
      await apiService.post(`/provider/notifications/${id}/read`, {});
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );
    } catch {
      // non-critical
    }
  };

  const handleRequestPayout = async () => {
    try {
      const response = await apiService.post('/provider/payouts/request', {});
      if (response.success) {
        toast({ title: 'Success', description: 'Payout request submitted successfully' });
        fetchDashboardData();
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to request payout', variant: 'destructive' });
    }
  };

  const handleStartWork = async (jobId: number) => {
    try {
      const response = await apiService.post(`/provider/jobs/${jobId}/start`, {});
      if (response.success) {
        toast({ title: 'Success', description: 'Work started! Job is now in progress.' });
        fetchDashboardData();
      } else {
        throw new Error(response.message || 'Failed to start work');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to start work',
        variant: 'destructive',
      });
    }
  };

  const handleCompleteWork = async (jobId: number) => {
    try {
      const response = await apiService.post(`/provider/jobs/${jobId}/complete`, {});
      if (response.success) {
        toast({ title: 'Success', description: 'Job marked as complete!' });
        fetchDashboardData();
      } else {
        throw new Error(response.message || 'Failed to complete work');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to complete work',
        variant: 'destructive',
      });
    }
  };

  const getTimeRemaining = (job: Job) => {
    if (!job.created_at) return 'N/A';
    const created = new Date(job.created_at).getTime();
    const now = new Date().getTime();
    const slaMinutes = job.sla_minutes || 60;
    const elapsed = Math.floor((now - created) / 60000);
    const remaining = Math.max(0, slaMinutes - elapsed);
    if (remaining === 0) return 'EXPIRED';
    if (remaining < 60) return `EXPIRES IN ${remaining}MIN`;
    const hours = Math.floor(remaining / 60);
    const mins = remaining % 60;
    return `EXPIRES IN ${hours}h ${mins}m`;
  };

  const pendingJobs = jobs.filter((j) => j.status === 'pending');
  const unreadCount = notifications.filter((n) => !n.read_at).length;

  const thisMonth = earningsSummary?.this_month_gbp ? `$${parseFloat(earningsSummary.this_month_gbp).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '£0.00';
  const availableNow = earningsSummary?.pending_gbp ? `$${parseFloat(earningsSummary.pending_gbp).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '£0.00';

  const perfGrade = performance.completionRate >= 95 ? 'EXCELLENT' : performance.completionRate >= 80 ? 'GOOD' : 'IMPROVING';
  const perfColor = perfGrade === 'EXCELLENT' ? 'bg-green-100 text-green-800' : perfGrade === 'GOOD' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800';

  if (authLoading) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </ProviderLayout>
    );
  }

  if (!user || user.role !== 'provider') {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600">Not authorized</p>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <Helmet>
        <title>Dashboard - VireWire Provider</title>
      </Helmet>

      <div className="space-y-4 md:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-xs md:text-sm text-gray-600 mt-1">
              Welcome back, {user?.first_name || 'Provider'}. You have{' '}
              {stats.awaitingResponse} job{stats.awaitingResponse !== 1 ? 's' : ''} awaiting response.
            </p>
          </div>
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            {/* Notification Bell */}
            <Popover open={notifOpen} onOpenChange={setNotifOpen}>
              <PopoverTrigger asChild>
                <button className="relative p-2 rounded-full hover:bg-gray-100 transition-colors">
                  <Bell className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0 shadow-xl" align="end">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <h3 className="font-semibold text-sm text-gray-900">Notifications</h3>
                  {unreadCount > 0 && (
                    <Badge className="bg-red-100 text-red-700 text-xs">{unreadCount} new</Badge>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
                  {notifications.length === 0 ? (
                    <div className="py-8 text-center text-sm text-gray-500">No notifications</div>
                  ) : (
                    notifications.slice(0, 10).map((n) => (
                      <div
                        key={n.id}
                        className={`px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${!n.read_at ? 'bg-blue-50' : ''}`}
                        onClick={() => handleMarkNotifRead(n.id)}
                      >
                        <p className="text-xs font-semibold text-gray-900">{n.title || 'Notification'}</p>
                        <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {new Date(n.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    ))
                  )}
                </div>
                {notifications.length > 0 && (
                  <div className="px-4 py-2 border-t text-center">
                    <button className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                      View all notifications
                    </button>
                  </div>
                )}
              </PopoverContent>
            </Popover>

            <Settings
              className="w-4 h-4 md:w-5 md:h-5 text-gray-600 cursor-pointer hover:text-gray-900"
              onClick={() => navigate('/provider/profile')}
            />
            <Avatar className="w-8 h-8 md:w-10 md:h-10 cursor-pointer" onClick={() => navigate('/provider/profile')}>
              <AvatarImage src={user?.avatar_url} />
              <AvatarFallback className="bg-green-100 text-green-700 font-semibold">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="p-4 md:p-6">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Awaiting Response</p>
              <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">{stats.awaitingResponse}</p>
              <p className="text-xs text-gray-500 mt-2">Respond within SLA</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4 md:p-6">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Active Jobs Today</p>
              <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">{stats.activeJobsToday}</p>
              <p className="text-xs text-gray-500 mt-2">
                {activeJobs.filter((j) => j.status === 'in_progress').length} in progress
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-600">
            <CardContent className="p-4 md:p-6">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Pending Payout</p>
              <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-green-600">
                ${stats.pendingPayout.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                {stats.pendingPayout > 0 ? 'Available to request' : 'No pending payout'}
              </p>
            </CardContent>
          </Card>

          <Card className={`border-l-4 ${stats.complianceAlerts > 0 ? 'border-l-red-500' : 'border-l-teal-500'}`}>
            <CardContent className="p-4 md:p-6">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Compliance Alerts</p>
              <p className={`text-2xl md:text-3xl lg:text-4xl font-bold ${stats.complianceAlerts > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {stats.complianceAlerts}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                {stats.complianceAlerts > 0 ? 'Action required' : 'All documents verified'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-4 md:space-y-6">
            {/* New Job Requests */}
            <div>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-1">New Job Requests</h2>
              <p className="text-xs md:text-sm text-gray-600 mb-4">Accept or decline within SLA window</p>

              {loading ? (
                <Card><CardContent className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></CardContent></Card>
              ) : pendingJobs.length === 0 ? (
                <Card>
                  <CardContent className="p-6 md:p-8 text-center">
                    <FileText className="w-10 h-10 md:w-12 md:h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm md:text-base text-gray-500">No new job requests</p>
                    <p className="text-xs text-gray-400 mt-1">New jobs will appear here when assigned</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3 md:space-y-4">
                  {pendingJobs.slice(0, 3).map((job) => (
                    <Card key={job.id} className="border border-gray-200 hover:shadow-md transition-shadow">
                      <CardContent className="p-4 md:p-6">
                        <div className="space-y-3 md:space-y-4">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 md:mb-2">
                                <Wrench className="w-4 h-4 md:w-5 md:h-5 text-blue-600 flex-shrink-0" />
                                <h3 className="font-bold text-sm md:text-base text-gray-900 truncate">
                                  {job.customer_name} — {job.service}
                                </h3>
                              </div>
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                Ref: #{job.job_reference || job.id} · {job.postcode}
                              </p>
                            </div>
                            <Badge className="bg-yellow-100 text-yellow-800 whitespace-nowrap w-fit">
                              <Clock className="w-3 h-3 mr-1" />
                              <span className="text-xs">Awaiting Response</span>
                            </Badge>
                          </div>

                          <div className="bg-yellow-50 border border-yellow-200 rounded px-3 py-2 text-xs font-semibold text-yellow-700">
                            ⏱ {getTimeRemaining(job)}
                          </div>

                          <div className="space-y-2 text-xs md:text-sm">
                            <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                              <span className="text-gray-500 uppercase font-medium tracking-wide text-[11px]">Schedule</span>
                              <span className="font-semibold text-gray-900">
                                {new Date(job.booking_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} · {job.booking_time_start}
                              </span>
                            </div>
                            <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                              <span className="text-gray-500 uppercase font-medium tracking-wide text-[11px]">Duration</span>
                              <span className="font-semibold text-gray-900">
                                {job.estimated_duration ? `${job.estimated_duration} min est.` : 'Not specified'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-500 uppercase font-medium tracking-wide text-[11px]">Quote</span>
                              <span className="font-bold text-lg text-gray-900">
                                ${Number(job.total_amount).toFixed(2)}
                              </span>
                            </div>
                          </div>

                          <div className="flex flex-col sm:flex-row gap-2 pt-2">
                            <Button
                              onClick={() => handleAcceptJob(job.id)}
                              disabled={acceptingJobId === job.id}
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs md:text-sm"
                            >
                              {acceptingJobId === job.id ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Accepting...</>
                              ) : 'Accept Job'}
                            </Button>
                            <Button
                              onClick={() => openCounterOffer(job.id, Number(job.total_amount))}
                              variant="outline"
                              className="flex-1 text-xs md:text-sm"
                            >
                              Counter-Offer
                            </Button>
                            <Button
                              onClick={() => handleDeclineJob(job.id)}
                              variant="outline"
                              className="flex-1 text-red-600 border-red-200 hover:bg-red-50 text-xs md:text-sm"
                            >
                              Decline
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {pendingJobs.length > 3 && (
                    <button
                      onClick={() => navigate('/provider/available-jobs')}
                      className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium py-2"
                    >
                      View {pendingJobs.length - 3} more pending jobs →
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Active Jobs Table */}
            {activeJobs.length > 0 && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                  <h2 className="text-lg md:text-xl font-bold text-gray-900">Active Jobs</h2>
                  <Button
                    onClick={() => navigate('/provider/active-jobs')}
                    variant="ghost"
                    className="text-blue-600 hover:text-blue-700 p-0 h-auto text-xs md:text-sm w-fit"
                  >
                    View All <ChevronRight className="w-3 h-3 md:w-4 md:h-4 ml-1" />
                  </Button>
                </div>

                <Card>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs md:text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">ID / Service</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">Location</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">Time</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">Status</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeJobs.slice(0, 3).map((job) => (
                          <tr key={job.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3">
                              <p className="font-semibold">#{job.id}</p>
                              <p className="text-gray-500 text-[11px]">{job.service}</p>
                            </td>
                            <td className="px-4 py-3 text-gray-600">{job.postcode}</td>
                            <td className="px-4 py-3 text-gray-600">
                              {job.booking_time_start}
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={job.status === 'in_progress' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
                                <span className={`w-2 h-2 rounded-full mr-1.5 inline-block ${job.status === 'in_progress' ? 'bg-purple-600' : 'bg-blue-600'}`} />
                                {job.status === 'in_progress' ? 'In Progress' : 'Accepted'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              {job.status === 'accepted' ? (
                                <Button onClick={() => handleStartWork(job.id)} size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs">
                                  Start Work
                                </Button>
                              ) : job.status === 'in_progress' ? (
                                <div className="flex gap-2">
                                  <Button onClick={() => handleCompleteWork(job.id)} size="sm" className="bg-orange-600 hover:bg-orange-700 text-white text-xs">
                                    Complete
                                  </Button>
                                  <Button onClick={() => navigate(`/provider/job/${job.id}`)} size="sm" variant="outline" className="text-xs">
                                    Details
                                  </Button>
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-3 md:space-y-4">
            {/* Documents Status */}
            <Card className={`border-l-4 ${stats.complianceAlerts > 0 ? 'border-l-red-500' : 'border-l-green-600'}`}>
              <CardContent className="p-4 md:p-6">
                <div className="flex items-start gap-2 md:gap-3">
                  {stats.complianceAlerts > 0 ? (
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 mb-1 text-sm">
                      {stats.complianceAlerts > 0 ? `${stats.complianceAlerts} document(s) expiring soon` : 'All documents verified'}
                    </h3>
                    <p className="text-xs text-gray-600 mb-2">
                      {stats.complianceAlerts > 0
                        ? 'Please renew expiring documents to stay compliant.'
                        : 'Your compliance profile is up to date.'}
                    </p>
                    <Button
                      onClick={() => navigate('/provider/documents')}
                      variant="link"
                      className={`${stats.complianceAlerts > 0 ? 'text-red-600' : 'text-green-600'} p-0 h-auto text-xs`}
                    >
                      MANAGE DOCUMENTS →
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payout Summary */}
            <Card>
              <CardContent className="p-4 md:p-6">
                <h3 className="font-bold text-gray-900 mb-3 text-sm">Payout Summary</h3>
                <div className="space-y-3 pb-3 border-b border-gray-200">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">This Month</p>
                    <p className="text-2xl font-bold text-gray-900">{thisMonth}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 uppercase font-semibold mb-1">Available Now</p>
                    <p className="text-2xl font-bold text-green-600">{availableNow}</p>
                  </div>
                  {earningsSummary && (
                    <div>
                      <p className="text-xs text-gray-500">
                        {earningsSummary.total_jobs} completed job{earningsSummary.total_jobs !== 1 ? 's' : ''} total
                      </p>
                    </div>
                  )}
                </div>
                <Button
                  onClick={handleRequestPayout}
                  disabled={parseFloat(earningsSummary?.pending_gbp || '0') <= 0}
                  className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white text-xs disabled:opacity-50"
                >
                  Request Payout
                </Button>
              </CardContent>
            </Card>

            {/* Performance */}
            <Card>
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-gray-900 text-sm">Performance</h3>
                  <Badge className={`text-xs ${perfColor}`}>{perfGrade}</Badge>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-gray-600">Completion Rate</p>
                      <p className="text-sm font-bold text-gray-900">{performance.completionRate || 0}%</p>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className="bg-green-600 h-1.5 rounded-full transition-all"
                        style={{ width: `${Math.min(performance.completionRate || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-gray-600">On-Time Rate</p>
                      <p className="text-sm font-bold text-gray-900">{performance.onTimeRate || 0}%</p>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className="bg-blue-600 h-1.5 rounded-full transition-all"
                        style={{ width: `${Math.min(performance.onTimeRate || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <p className="text-xs text-gray-600">Customer Rating</p>
                    <div className="flex items-center gap-1">
                      <p className="text-xl font-bold text-gray-900">{performance.rating ? Number(performance.rating).toFixed(1) : '—'}</p>
                      <Star className={`w-4 h-4 ${performance.rating > 0 ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/provider/performance')}
                  variant="link"
                  className="text-blue-600 p-0 h-auto text-xs mt-2 w-full text-left"
                >
                  View Full Report →
                </Button>
              </CardContent>
            </Card>

            {/* Earnings Chart */}
            <Card>
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-gray-900 text-sm">Weekly Earnings</h3>
                  <TrendingUp className="w-4 h-4 text-green-600" />
                </div>
                {weeklyEarnings.length > 0 && weeklyEarnings.some((w) => w.amount > 0) ? (
                  <ResponsiveContainer width="100%" height={120}>
                    <BarChart data={weeklyEarnings} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                      <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
                      <Tooltip
                        formatter={(value: number) => [`$${value.toFixed(2)}`, 'Earnings']}
                        contentStyle={{ fontSize: 11, borderRadius: 6 }}
                      />
                      <Bar dataKey="amount" fill="#16a34a" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-24 bg-gray-50 rounded-lg flex items-center justify-center">
                    <p className="text-xs text-gray-400">No earnings data yet</p>
                  </div>
                )}
                <Button
                  onClick={() => navigate('/provider/earnings')}
                  variant="link"
                  className="text-blue-600 p-0 h-auto text-xs mt-2 w-full text-left"
                >
                  View Earnings →
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 py-3 border-t border-gray-200 mt-4">
          <p>© {new Date().getFullYear()} Virewire Provider Portal. All operational data is reviewed periodically.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-1">
            <a href="#" className="hover:text-gray-700">Operational SLA</a>
            <span className="hidden sm:inline">·</span>
            <a href="#" onClick={() => navigate('/provider/support')} className="hover:text-gray-700 cursor-pointer">Support Center</a>
          </div>
        </div>
      </div>

      {/* Counter-Offer Modal */}
      <Dialog open={counterOfferOpen} onOpenChange={setCounterOfferOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Submit Counter-Offer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="co-amount">Your Proposed Amount ($)</Label>
              <Input
                id="co-amount"
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.00"
                value={counterOffer.amount}
                onChange={(e) => setCounterOffer((prev) => ({ ...prev, amount: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="co-notes">Reason / Notes (optional)</Label>
              <Textarea
                id="co-notes"
                placeholder="Explain your counter-offer reason..."
                rows={3}
                value={counterOffer.notes}
                onChange={(e) => setCounterOffer((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCounterOfferOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitCounterOffer}
              disabled={submittingCounterOffer || !counterOffer.amount}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {submittingCounterOffer ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</> : 'Submit Counter-Offer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ProviderLayout>
  );
};

export default ProviderDashboardNew;
